import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import react, { createElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Flex, Image, Spinner, Text, Toast } from "pcln-design-system";
import { History, SuccessOutline } from "pcln-icons";
import { DialogLoading } from "./DialogLoading.js";
import { ScrollIntoView } from "./ScrollIntoView.js";
import TryAgain from "./components/TryAgain/index.js";
import { ChatbotDataContext } from "./context/ChatbotDataContext.js";
import { useDialogStylesContext } from "./hooks/useDialogStylesContext.js";
import { submitChatMessages } from "./submitChatMessages.js";
import { ChatHistoryControl, ChatHistoryControlWrapper, ToastWrapper } from "./ChatContent.styles.js";
import { ChatMessagesList } from "./ChatMessagesCollections.js";
import Loading from "./components/Loading/index.js";
import { inlineMetadataManager } from "./components/MarkdownComponent/MetadataManager.js";
import PennyExpandableInput from "./components/PennyExpandableInput/index.js";
import { GREETING_MESSAGE } from "./constants/prompt.js";
import { useStandaloneStyles } from "./context/StandaloneStyleContext.js";
import { useChatMessagesContext } from "./hooks/useChatMessagesContext.js";
import { useChatUIContext } from "./hooks/useChatUIContext.js";
import { useLoadingStateContext } from "./hooks/useLoadingStateContext.js";
import { useMarkerComponentMemo } from "./hooks/useMarkerComponentMemo.js";
import ClearHistory from "./svgs/ClearHistory.js";
import { browserStorageManager } from "./utils/browserStorage.js";
import { getLastUserMessage } from "./utils/getLastUserMessage.js";
import { removePennyInitialMessageKeys } from "./utils/removePennyInitialMessageKeys.js";
import { resolveChatContentHeight, resolveChatContentStyle } from "./utils/resolveChatContentVariables.js";
const EXPANDABLE_INPUT_INIT_HEIGHT = 30;
const TRAVEL_BOT_LOGO = 'https://s1.pclncdn.com/design-assets/pcln-bot/logos/pcln_bot_opener_logo_no_border_v1.1.svg';
const DUMMY_HELLO_MESSAGE = 'Hello!!!';
const SEPARATOR_MESSAGE = '<SEPARATOR>';
const ChatMainContentSection = ({ retrievalLoading = false, visibilityChannel })=>{
    const { setLoadMoreMessages, submitChatMessagesPayload, handleMarkerResponse, fetchRequestOptions, markerComponents, errorMessage, setErrorMessage, setIsDisablingInputValue, fullStreamingTexts, setStreamingText, debugMode, discoveryCitySelected, pushPrompt, loadMoreMessages, isDisablingInput, accountLessAccessToken, accountLessTripsAccessPayload, isMobile } = useContext(ChatbotDataContext);
    if (accountLessTripsAccessPayload) submitChatMessagesPayload.accountLessTripsAccessPayload = accountLessTripsAccessPayload;
    const memoMarkerComponents = useMarkerComponentMemo(markerComponents);
    const { chatMessages, setChatMessages } = useChatMessagesContext();
    const { openErrorComponent, setOpenErrorComponent } = useChatUIContext();
    const [expandableTextHeight, setExpandableTextHeight] = useState(EXPANDABLE_INPUT_INIT_HEIGHT);
    const { isLoading, setLoadingStatus, loadingStatusMessages, setLoadingStatusMessage, setLoadingHotelSkeleton } = useLoadingStateContext();
    const contentRef = useRef(null);
    const messagesEndRef = useRef(null);
    const loadRef = useRef(null);
    const [lastMessage, setLastMessage] = useState('');
    const [showClearedHistory, setShowClearedHistory] = useState(false);
    const [isClearedHistoryActive, setIsClearedHistoryActive] = useState(false);
    const [currScrollHeight, setCurrScrollHeight] = useState(0);
    const { isSessionActive, hotelPayload, productId } = submitChatMessagesPayload;
    const isAudioPlaybackEnabledRef = useRef(false);
    const { isShowMoreListingCards = false } = hotelPayload ?? {};
    const handleExpandableInputHeight = useCallback((height)=>{
        setExpandableTextHeight(height);
    }, []);
    useEffect(()=>{
        if (chatMessages) {
            const userMessage = getLastUserMessage(chatMessages);
            if (userMessage) setLastMessage(userMessage.content);
        }
    }, [
        chatMessages.length
    ]);
    useEffect(()=>{
        if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest'
        });
    }, [
        expandableTextHeight
    ]);
    const submitChatMessagesOptions = {
        setChatMessages,
        setStreamingText,
        setLoadingStatus,
        setErrorMessage,
        setIsDisablingInputValue,
        requestOptions: {
            ...fetchRequestOptions,
            headers: {
                ...fetchRequestOptions?.headers,
                ...accountLessAccessToken ? {
                    'account-less-access-token': accountLessAccessToken
                } : {}
            }
        },
        markerComponents,
        handleMarkerResponse,
        setLoadingStatusMessage,
        setLoadingHotelSkeleton
    };
    useEffect(()=>{
        if (discoveryCitySelected || pushPrompt && !retrievalLoading) {
            const chatContent = discoveryCitySelected ? `Looking for hotels in ${discoveryCitySelected}` : pushPrompt;
            const chatMessage = {
                content: chatContent,
                date: new Date(),
                role: 'user'
            };
            const promptMessage = {
                content: chatContent,
                role: 'user'
            };
            const updatedChatMessages = [
                ...chatMessages,
                chatMessage
            ];
            setChatMessages(updatedChatMessages);
            submitChatMessages({
                submitChatMessagesPayload: {
                    ...submitChatMessagesPayload,
                    includeSpeech: isAudioPlaybackEnabledRef.current,
                    prompts: [
                        promptMessage
                    ]
                },
                setOpenErrorComponent,
                chatMessages: updatedChatMessages,
                ...submitChatMessagesOptions,
                requestOptions: {
                    ...submitChatMessagesOptions.requestOptions,
                    headers: {
                        ...submitChatMessagesOptions.requestOptions.headers,
                        ...accountLessAccessToken ? {
                            'account-less-access-token': accountLessAccessToken
                        } : {}
                    }
                }
            });
        }
    }, [
        discoveryCitySelected,
        pushPrompt
    ]);
    useEffect(()=>{
        const onScroll = ()=>{
            const scrollableDiv = contentRef.current;
            if (!scrollableDiv) return;
        };
        if (contentRef.current) contentRef.current.addEventListener('scroll', onScroll);
        return ()=>{
            if (contentRef.current) contentRef.current.removeEventListener('scroll', onScroll);
        };
    }, []);
    useEffect(()=>{
        const curHeight = contentRef?.current?.scrollHeight;
        if (contentRef.current) contentRef.current.scrollTop = curHeight - currScrollHeight;
    }, [
        loadMoreMessages
    ]);
    const userMessages = useMemo(()=>chatMessages?.filter((message)=>message?.role === 'user' && message?.content) ?? [], [
        chatMessages
    ]);
    useEffect(()=>{
        const submitChatMessagePayloadObj = {
            setOpenErrorComponent,
            chatMessages,
            ...submitChatMessagesOptions,
            requestOptions: {
                ...submitChatMessagesOptions.requestOptions,
                headers: {
                    ...submitChatMessagesOptions.requestOptions.headers,
                    ...accountLessAccessToken ? {
                        'account-less-access-token': accountLessAccessToken
                    } : {}
                }
            }
        };
        const isNewMessageWithSession = !browserStorageManager.getBrowserStorage()?.isInitialMessageSubmitted() && !retrievalLoading || chatMessages?.length === 1 && chatMessages?.[0]?.displayComponentName === 'SESSION_INITIALIZED';
        if (isNewMessageWithSession && !pushPrompt && -1 !== submitChatMessagesPayload.productId) {
            browserStorageManager.getBrowserStorage()?.setInitialMessageSubmitted();
            submitChatMessages({
                submitChatMessagesPayload: {
                    ...submitChatMessagesPayload,
                    prompts: [
                        {
                            role: 'user',
                            content: DUMMY_HELLO_MESSAGE
                        }
                    ]
                },
                ...submitChatMessagePayloadObj
            });
        }
    }, [
        retrievalLoading
    ]);
    const previousErrorState = useRef(openErrorComponent);
    useEffect(()=>{
        if (previousErrorState.current === openErrorComponent) return;
        previousErrorState.current = openErrorComponent;
        const curHeight = contentRef?.current?.scrollHeight;
        setIsDisablingInputValue(openErrorComponent && !isDisablingInput);
        if (chatMessages?.length > 0 && chatMessages[chatMessages.length - 1]?.role === 'user') {
            const userMessage = getLastUserMessage(chatMessages);
            if (userMessage) submitChatMessages({
                submitChatMessagesPayload: {
                    ...submitChatMessagesPayload,
                    prompts: [
                        userMessage
                    ],
                    includeSpeech: false
                },
                setOpenErrorComponent,
                chatMessages,
                ...submitChatMessagesOptions,
                requestOptions: {
                    ...submitChatMessagesOptions.requestOptions,
                    headers: {
                        ...submitChatMessagesOptions.requestOptions.headers,
                        ...accountLessAccessToken ? {
                            'account-less-access-token': accountLessAccessToken
                        } : {}
                    }
                }
            });
            if (contentRef.current) contentRef.current.scrollTop = curHeight - currScrollHeight;
        }
    }, [
        openErrorComponent
    ]);
    useEffect(()=>{
        if (!isLoading && isClearedHistoryActive && !showClearedHistory) {
            setShowClearedHistory(true);
            setTimeout(()=>{
                setIsClearedHistoryActive(false);
                setShowClearedHistory(false);
            }, 1000);
        }
    }, [
        isClearedHistoryActive
    ]);
    const handleViewHistory = useCallback(()=>{
        setLoadMoreMessages(true);
        setCurrScrollHeight(contentRef?.current?.scrollHeight);
    }, []);
    const handleClearHistoryClick = useCallback(()=>{
        handleClearHistory();
        setLoadMoreMessages(true);
    }, [
        handleClearHistory
    ]);
    function submitChatMessageInMarkerComponent(msg, updatedChatMessage, headers) {
        if (!msg.content && msg.displayComponentName) setChatMessages([
            ...chatMessages,
            msg
        ]);
        else submitChatMessages({
            submitChatMessagesPayload: {
                ...submitChatMessagesPayload,
                prompts: [
                    msg
                ]
            },
            setOpenErrorComponent,
            chatMessages: Array.isArray(updatedChatMessage) && 0 !== updatedChatMessage.length ? updatedChatMessage : chatMessages,
            ...submitChatMessagesOptions,
            requestOptions: {
                ...submitChatMessagesOptions.requestOptions,
                headers: {
                    ...submitChatMessagesOptions.requestOptions.headers,
                    ...headers || {}
                }
            }
        });
    }
    function handleClearHistory() {
        const submitChatMessagePayloadObj = {
            setOpenErrorComponent,
            chatMessages: [],
            ...submitChatMessagesOptions,
            requestOptions: {
                ...submitChatMessagesOptions.requestOptions,
                headers: {
                    ...submitChatMessagesOptions.requestOptions.headers,
                    ...accountLessAccessToken ? {
                        'account-less-access-token': accountLessAccessToken
                    } : {}
                }
            }
        };
        browserStorageManager.getBrowserStorage()?.removeItem('pennysid');
        removePennyInitialMessageKeys();
        setChatMessages([]);
        setIsClearedHistoryActive(true);
        inlineMetadataManager.reset();
        submitChatMessages({
            submitChatMessagesPayload: {
                ...submitChatMessagesPayload,
                prompts: [
                    {
                        role: 'user',
                        content: DUMMY_HELLO_MESSAGE
                    }
                ]
            },
            ...submitChatMessagePayloadObj
        });
    }
    const dialogStyles = useDialogStylesContext();
    const standaloneStyles = useStandaloneStyles();
    const isDialogStylesEmpty = !dialogStyles?.topLevelBoxStyle || 0 === Object.keys(dialogStyles.topLevelBoxStyle).length;
    const styles = resolveChatContentStyle({
        isDialogStylesEmpty,
        dialogStyles,
        standaloneStyles
    });
    const excludedFirstMessages = [
        GREETING_MESSAGE,
        SEPARATOR_MESSAGE
    ];
    const shouldLoadMorePrompts = -1 === productId || !loadMoreMessages && isSessionActive && !excludedFirstMessages.includes(chatMessages?.[0]?.content) && chatMessages?.[0]?.displayComponentName !== 'SESSION_INITIALIZED';
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            retrievalLoading && isSessionActive ? /*#__PURE__*/ jsx(Loading, {}) : /*#__PURE__*/ createElement(Box, {
                ...styles.topLevelBoxStyle,
                height: resolveChatContentHeight({
                    isDialogStylesEmpty,
                    expandableTextHeight
                }),
                key: "chat-dialog-transcript-container",
                ref: contentRef,
                children: [
                    showClearedHistory ? /*#__PURE__*/ jsx(ToastWrapper, {
                        width: '300px',
                        children: /*#__PURE__*/ jsx(Toast, {
                            color: "success",
                            colorScheme: "successLight",
                            icon: /*#__PURE__*/ jsx(SuccessOutline, {}),
                            hideClose: true,
                            children: "Chat History Cleared"
                        })
                    }) : null,
                    /*#__PURE__*/ jsxs(Flex, {
                        ...styles.contentFlexStyle,
                        children: [
                            /*#__PURE__*/ jsxs(ChatHistoryControlWrapper, {
                                ref: loadRef,
                                children: [
                                    shouldLoadMorePrompts && /*#__PURE__*/ jsx(ChatHistoryControl, {
                                        onClick: handleViewHistory,
                                        children: /*#__PURE__*/ jsxs(Button, {
                                            variation: 'link',
                                            children: [
                                                /*#__PURE__*/ jsx(History, {
                                                    size: 20,
                                                    mr: 2
                                                }),
                                                " View History"
                                            ]
                                        })
                                    }),
                                    isSessionActive && userMessages?.length > 0 && /*#__PURE__*/ jsx(ChatHistoryControl, {
                                        onClick: handleClearHistoryClick,
                                        children: /*#__PURE__*/ jsxs(Button, {
                                            variation: 'link',
                                            ml: 4,
                                            children: [
                                                /*#__PURE__*/ jsx(ClearHistory, {
                                                    size: 15
                                                }),
                                                " ",
                                                /*#__PURE__*/ jsx(Text, {
                                                    ml: 2,
                                                    children: "Clear History"
                                                })
                                            ]
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ jsx(Flex, {
                                justifyContent: 'center',
                                children: loadMoreMessages ? /*#__PURE__*/ jsx(Box, {
                                    mb: 4,
                                    children: /*#__PURE__*/ jsx(Spinner, {
                                        size: 40,
                                        color: 'purple',
                                        children: /*#__PURE__*/ jsx(Image, {
                                            src: TRAVEL_BOT_LOGO,
                                            width: 28,
                                            alt: "Penny chat avatar"
                                        })
                                    })
                                }) : null
                            }),
                            /*#__PURE__*/ jsx(ChatMessagesList, {
                                messagesEndRef: messagesEndRef,
                                isStandalone: isDialogStylesEmpty,
                                chatMessages: chatMessages,
                                markerComponents: memoMarkerComponents,
                                isLoading: isLoading,
                                debugMode: debugMode,
                                setIsDisablingInputValue: setIsDisablingInputValue,
                                setChatMessages: setChatMessages,
                                submitMessages: submitChatMessageInMarkerComponent,
                                submitChatMessageInMarkerComponent: submitChatMessageInMarkerComponent,
                                isShowMoreListingCards: isShowMoreListingCards,
                                submitQuickActionMessage: function(action) {
                                    const { chatContent, promptContent } = action;
                                    const promptMessage = {
                                        content: promptContent || chatContent,
                                        role: 'user'
                                    };
                                    const chatMessage = {
                                        content: chatContent,
                                        role: 'user'
                                    };
                                    const updatedChatMessages = [
                                        ...chatMessages,
                                        chatMessage
                                    ];
                                    setChatMessages(updatedChatMessages);
                                    submitChatMessages({
                                        submitChatMessagesPayload: {
                                            ...submitChatMessagesPayload,
                                            prompts: [
                                                promptMessage
                                            ]
                                        },
                                        setOpenErrorComponent,
                                        chatMessages: updatedChatMessages,
                                        ...submitChatMessagesOptions,
                                        requestOptions: {
                                            ...submitChatMessagesOptions.requestOptions,
                                            headers: {
                                                ...submitChatMessagesOptions.requestOptions.headers,
                                                ...accountLessAccessToken ? {
                                                    'account-less-access-token': accountLessAccessToken
                                                } : {}
                                            }
                                        }
                                    });
                                },
                                clearActionsData: function() {
                                    setChatMessages(chatMessages.filter((msg)=>'STRUCTURED_DATA' !== msg.displayComponentName));
                                },
                                isMobile: isMobile
                            }),
                            errorMessage && openErrorComponent && /*#__PURE__*/ jsx(ScrollIntoView, {
                                children: /*#__PURE__*/ jsx(TryAgain, {
                                    lastMessage: lastMessage
                                })
                            }),
                            !isAudioPlaybackEnabledRef.current && isLoading && /*#__PURE__*/ jsx(ScrollIntoView, {
                                children: /*#__PURE__*/ jsx(DialogLoading, {
                                    streamingText: fullStreamingTexts,
                                    lastMessage: lastMessage,
                                    loadingStatusMessages: loadingStatusMessages,
                                    isMobile: isMobile
                                })
                            })
                        ]
                    }),
                    isDialogStylesEmpty ? /*#__PURE__*/ jsx("div", {
                        ref: messagesEndRef,
                        style: {
                            height: 0
                        }
                    }) : /*#__PURE__*/ jsx(Fragment, {})
                ]
            }),
            /*#__PURE__*/ jsx(PennyExpandableInput, {
                submitChatMessagesOptions: submitChatMessagesOptions,
                isStandalone: isDialogStylesEmpty,
                styles: styles,
                handleExpandableInputHeight: handleExpandableInputHeight
            })
        ]
    });
};
const ChatContent = /*#__PURE__*/ react.memo(ChatMainContentSection);
export { ChatContent as default };
